import ModalForm from "../modal-form";
import React, { useEffect, useState } from "react";
import { obterIdTenent, usuarioTemPermissao } from "../../services/seguranca";
import { TipoFormulario } from "../../services/constantes";
import { ApiError, Campo, MultaLancameto, OpcoesPeriodicidadePagamento } from "../../services/tipos";
import { useAppSelector } from "../../store/hooks";
import { AxiosError } from "axios";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FaCarSide, FaUser, FaFileInvoiceDollar, FaCalendarAlt } from "react-icons/fa";

interface ModalGerarLancamentoMultaProps {
    show: boolean;
    onClose: () => void;
    entidade: any;
}

const ModalGerarLancamentoMulta: React.FC<ModalGerarLancamentoMultaProps> = ({
    show,
    onClose,
    entidade,
}) => {
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [token] = useState(useAppSelector(state => state.token));
    const [entidadeInicial, setEntidadeInicial] = useState<any>({});
    const [forcarAtualizacaoDeEntidadeNoModal, setForcarAtualizacaoDeEntidadeNoModal] = useState<boolean>(false);

    useEffect(() => {
        if (show) {
            setEntidadeInicial({
                ...entidade,
                valorFinal: entidade.valor,
                idVeiculo: entidade?.idVeiculo ?? '',
                idLocatario: entidade?.locacao.locatario.id ?? '',
                dataInicio: `${new Date().getUTCFullYear()}-${(new Date().getUTCMonth() + 1)
                    .toString()
                    .padStart(2, "0")}-${new Date().getUTCDate().toString().padStart(2, "0")}`,
            });
        }
    }, [show, entidade]);

    useEffect(() => {
        setForcarAtualizacaoDeEntidadeNoModal(true);
    }, [entidadeInicial]);

    useEffect(() => {
        if (forcarAtualizacaoDeEntidadeNoModal) {
            setForcarAtualizacaoDeEntidadeNoModal(false);
        }
    }, [forcarAtualizacaoDeEntidadeNoModal]);

    const veiculo = entidadeInicial.veiculo;
    const locatario = entidadeInicial.locacao?.locatario;
    const valorFormatado = `R$ ${Number(entidadeInicial.valor || 0).toFixed(2).replace('.', ',')}`;
    const dataVencimento = entidadeInicial.dataHoraVencimento
        ? new Date(entidadeInicial.dataHoraVencimento).toLocaleDateString('pt-BR')
        : null;

    const ResumoMulta = () => (
        <div style={{
            padding: '0.8rem 1rem',
            borderRadius: '8px',
            background: '#e7f5ff',
            borderLeft: '5px solid #4dabf7',
            color: '#1864ab',
            fontSize: '0.88rem',
            lineHeight: '1.7',
        }}>
            <div style={{ fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.95rem' }}>Dados da Multa</div>
            {veiculo && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaCarSide size={14} /> <span><b>Veículo:</b> {veiculo.placa} - {veiculo.marca} {veiculo.modelo}</span>
                </div>
            )}
            {locatario && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaUser size={14} /> <span><b>Locatário:</b> {locatario.nome} ({locatario.documento})</span>
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaFileInvoiceDollar size={14} /> <span><b>Infração:</b> {entidadeInicial.descricaoInfracao} — <b>{valorFormatado}</b></span>
            </div>
            {dataVencimento && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <FaCalendarAlt size={14} /> <span><b>Vencimento:</b> {dataVencimento}</span>
                </div>
            )}
        </div>
    );

    const campos: Campo[] = [
        {
            coluna: 1, largura: 12, nome: '_resumo', tipo: 'custom', linha: 1, required: false, visible: true, label: '',
            component: () => <ResumoMulta />,
        },
        {
            coluna: 1, label: 'Desconto (%)', largura: 4, nome: 'desconto', tipo: 'percentual', linha: 2, required: false, visible: true,
            ajudinha: { texto: "Percentual de desconto sobre o valor da multa. Deixe 0 se não houver desconto." },
            onCampoDigitar(e) {
                const valorDescontoEmPorcetagem = e / 100;
                const percentualFinal = 1 - valorDescontoEmPorcetagem;
                const valorFinal = entidadeInicial.valor * percentualFinal;
                setEntidadeInicial((prevState: any) => ({
                    ...prevState,
                    valorFinal: valorFinal,
                    valorParcela: valorFinal / prevState.parcelas,
                }));
                setForcarAtualizacaoDeEntidadeNoModal(true);
            },
        },
        { coluna: 2, label: 'Valor a Cobrar', largura: 4, nome: 'valorFinal', tipo: 'money', linha: 2, required: false, visible: true, apenasLeitura: true,
            ajudinha: { texto: "Valor da multa já com o desconto aplicado." },
        },
        { coluna: 3, label: 'Valor de Cada Parcela', largura: 4, nome: 'valorParcela', tipo: 'money', linha: 2, required: false, visible: true, apenasLeitura: true,
            ajudinha: { texto: "Valor que o locatário vai pagar em cada parcela." },
        },
        {
            coluna: 1, label: 'Parcelas', largura: 4, nome: 'parcelas', tipo: 'select', linha: 3, required: true, visible: true,
            ajudinha: { texto: "Quantas vezes o locatário vai pagar. 1x = à vista." },
            opcoes: [
                { label: "1x (à vista)", valor: 1 },
                { label: "2x", valor: 2 },
                { label: "3x", valor: 3 },
                { label: "4x", valor: 4 },
                { label: "5x", valor: 5 },
                { label: "6x", valor: 6 },
                { label: "7x", valor: 7 },
                { label: "8x", valor: 8 },
                { label: "9x", valor: 9 },
                { label: "10x", valor: 10 },
                { label: "11x", valor: 11 },
                { label: "12x", valor: 12 },
            ], onCampoDigitar(e) {
                const opcao = Number(e);
                setEntidadeInicial((prevState: any) => ({
                    ...prevState,
                    parcelas: e,
                    valorParcela: prevState.valorFinal / opcao,
                }));
                setForcarAtualizacaoDeEntidadeNoModal(true);
            },
        },
        {
            coluna: 2, label: 'Cobrar a cada', largura: 4, nome: 'periodicidadePagamento', tipo: 'select', linha: 3, required: true, visible: (values: any) => values.parcelas > 1,
            ajudinha: { texto: "Intervalo entre uma parcela e outra." },
            opcoes: OpcoesPeriodicidadePagamento,
        },
        {
            coluna: 3, label: 'Primeiro vencimento', largura: 4, nome: 'dataInicio', tipo: 'date', linha: 3, required: true, visible: true,
            ajudinha: { texto: "Data de vencimento da primeira parcela (ou da parcela única, se for à vista)." },
        },
        {
            coluna: 1, label: 'Marcar multa como paga agora', largura: 6, nome: 'definirMultaComoPaga', tipo: 'custom', linha: 4, required: false, visible: true,
            component: ({ value, onChange, entidade }: any) => {
                const disabled = !!entidade?.quitacaoAutomatica;
                return (
                    <div style={{ opacity: disabled ? 0.5 : 1 }}>
                        <input type="checkbox" checked={!!value} disabled={disabled}
                            onChange={(e) => onChange(e.target.checked)}
                            style={{ marginRight: '0.5rem' }} />
                        <span style={{ fontSize: '0.9rem' }}>Marcar multa como paga agora</span>
                    </div>
                );
            },
            ajudinha: { texto: "Se marcado, a multa já fica com status 'Paga' assim que você salvar." },
        },
        {
            coluna: 2, label: 'Quitar multa ao receber tudo', largura: 6, nome: 'quitacaoAutomatica', tipo: 'custom', linha: 4, required: false, visible: true,
            component: ({ value, onChange, entidade }: any) => {
                const disabled = !!entidade?.definirMultaComoPaga;
                return (
                    <div style={{ opacity: disabled ? 0.5 : 1 }}>
                        <input type="checkbox" checked={!!value} disabled={disabled}
                            onChange={(e) => onChange(e.target.checked)}
                            style={{ marginRight: '0.5rem' }} />
                        <span style={{ fontSize: '0.9rem' }}>Quitar multa ao receber tudo</span>
                    </div>
                );
            },
            ajudinha: { texto: "Quando todas as parcelas forem pagas, a multa muda automaticamente para 'Paga'." },
        },
    ];

    const onSave = async (entidade: any) => {
        if (isSubmiting) return;

        setIsSubmiting(true);

        if (usuarioTemPermissao(token, 'lancamento_novo')) {
            entidade['dataHoraVencimento'] = null;
            const multaLancamentoASalvar: MultaLancameto = {
                idEmpresa: obterIdTenent(token),
                dataInicio: entidade['dataInicio'],
                definirMultaComoPaga: entidade['definirMultaComoPaga'] || false,
                quitacaoAutomatica: entidade['quitacaoAutomatica'] || false,
                desconto: entidade['desconto'] | 0,
                idMulta: entidadeInicial['id'],
                valor: entidadeInicial['valor'],
                descricaoInfracao: entidadeInicial['descricaoInfracao'],
                idLocacao: entidadeInicial['idLocacao'],
                idVeiculo: entidadeInicial['idVeiculo'],
                parcelas: entidade['parcelas'],
                periodicidadePagamento: entidade['parcelas'] > 1 ? entidade['periodicidadePagamento'] : 0,
            }

            api.post('/MultaLancamento/', multaLancamentoASalvar, {
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(response => {
                setTimeout(() => {
                    tratarSucesso();
                    setIsSubmiting(false);
                }, 250);
            }).catch((error: AxiosError<ApiError>) => {
                tratarErro(error);
            });
        }
    }

    const tratarErro = (error: AxiosError<ApiError>) => {
        onClose();
        const errorMessage = error.response?.data?.Mensagem || 'Erro desconhecido';
        toast.error(`Não foi possível continuar a operação Multa: ${errorMessage}`);
        setIsSubmiting(false);
    }

    const tratarSucesso = () => {
        onClose();
        toast.success("Lançamento(s) gerado(s) com sucesso!");
        setIsSubmiting(false);
    }

    return (
        <>
            <ModalForm
                titulo="Cobrar Multa do Locatário"
                apiConfig={{
                    endpoint: '/MultaLancamento/',
                    idTenentCampo: obterIdTenent(token),
                    requisicaoBemSucedida: tratarSucesso,
                    tratarErro: tratarErro,
                }}
                onSave={onSave}
                campos={campos}
                carregando={false}
                modo={TipoFormulario.tpCadastro}
                onCancel={onClose}
                onHide={onClose}
                show={show}
                tamanho="lg"
                isSaving={isSubmiting}
                ajudinha={{
                    texto: "Defina como o locatário vai pagar essa multa: à vista ou parcelado.",
                    autoCloseTime: 3,
                    ehDestacado: true,
                }}
                tituloBotaoOk="Gerar"
                atualizacaoEntidade={
                    {
                        entidadeUsadaParaAtualizacao: entidadeInicial,
                        forcarAtualizacao: forcarAtualizacaoDeEntidadeNoModal
                    }
                }
            />
        </>
    );
};

export default ModalGerarLancamentoMulta;